import React from 'react';
import { motion } from 'framer-motion';
import { GiftIcon, CheckIcon } from 'lucide-react';
export function BonusSection() {
  return <section className="bg-cream py-16 md:py-24" aria-labelledby="bonus-heading" data-id="element-1">
      <div className="max-w-5xl mx-auto px-4" data-id="element-2">
        {/* A. SECTION HEADER + TRANSITION COPY */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-3">
          <h2 id="bonus-heading" className="font-serif text-forest text-3xl md:text-4xl lg:text-5xl font-700 italic mb-8" data-id="element-4">
            "But I am Not Done Yet."
          </h2>
          <div className="font-sans text-stone-600 text-lg leading-relaxed max-w-3xl mx-auto space-y-4 text-left md:text-center" data-id="element-5">
            <p data-id="element-6">
              When I put together <em data-id="element-7">The Cortisol Reset Kitchen</em>, I knew
              the protocol itself was powerful.
            </p>
            <p data-id="element-8">But I also knew something else.</p>
            <p data-id="element-9">
              I knew that the hardest moments for women over 55 are not the
              Monday mornings when motivation is high.
            </p>
            <p data-id="element-10">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-11">Tuesday nights</strong>{' '}
              when everyone at the restaurant is ordering pasta and you do not
              know what to do.
            </p>
            <p data-id="element-12">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-13">11 PM moments</strong>{' '}
              when your stomach is growling and every diet rule you know says:{' '}
              <em data-id="element-14">just ignore it.</em>
            </p>
            <p data-id="element-15">
              They are the{' '}
              <strong className="font-700 text-forest" data-id="element-16">
                Wednesday mornings
              </strong>{' '}
              when your knees ache and you feel guilty for not doing cardio.
            </p>
            <p data-id="element-17">
              So I built three companion guides to cover exactly those moments.
            </p>
            <p data-id="element-18">
              And today, when you join <em data-id="element-19">The Cortisol Reset Kitchen</em>, you
              get all three of them{' '}
              <strong className="font-700 text-forest" data-id="element-20">completely free.</strong>
            </p>
          </div>
        </motion.div>

        {/* B. BONUS #1 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-12" data-id="element-21">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-22">
            <div className="order-2 md:order-1" data-id="element-23">
              <img src="https://cdn.magicpatterns.com/uploads/jYW7AwDJmZK8DYTcxYwyMP/heylloyd-image-1773015601413_port_bono1.png" alt="The Restaurant Survival Blueprint" className="w-full rounded-2xl shadow-md" data-id="element-24" />
            </div>
            <div className="order-1 md:order-2" data-id="element-25">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-26">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-27" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-28">
                  FREE BONUS #1 — Valued at $27
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-29">
                The Restaurant Survival Blueprint
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-30">
                "How to Dine Out Without Triggering a 48-Hour Cortisol Bloat"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-31">
                <p data-id="element-32">Most women over 55 quietly dread dinner invitations.</p>
                <p data-id="element-33">
                  Not because they do not want the friendship or the laughter or
                  the glass of wine.
                </p>
                <p data-id="element-34">But because they know what happens the next morning.</p>
                <p data-id="element-35">
                  The scale spikes. The waistband tightens. The bloating arrives
                  like clockwork. And they spend two days trying to undo one
                  dinner out.
                </p>
                <p data-id="element-36">
                  <strong className="font-700 text-forest" data-id="element-37">
                    Here is what nobody has told you: it is not the portion size
                    that is the problem.
                  </strong>
                </p>
                <p data-id="element-38">
                  It is the hidden cortisol triggers inside restaurant kitchens
                  — the seed oils, the sodium-loaded sauces, the blood sugar
                  roller coaster disguised as a healthy salad.
                </p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-39">
                The Restaurant Survival Blueprint gives you:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-40">
                <li className="flex items-start gap-3" data-id="element-41">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-42" />
                  <span className="font-sans text-stone-600" data-id="element-43">
                    <strong className="font-700 text-forest" data-id="element-44">
                      The 5-Second Menu Decoder
                    </strong>{' '}
                    — instantly identify the 3 ingredients that spike cortisol
                    at any restaurant
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-45">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-46" />
                  <span className="font-sans text-stone-600" data-id="element-47">
                    <strong className="font-700 text-forest" data-id="element-48">
                      The Cuisine-by-Cuisine Safe List
                    </strong>{' '}
                    — Italian, Mexican, Asian, American. You will never feel
                    lost at a menu again
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-49">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-50" />
                  <span className="font-sans text-stone-600" data-id="element-51">
                    <strong className="font-700 text-forest" data-id="element-52">
                      The Anti-Inflammatory Bar Order
                    </strong>{' '}
                    — what to drink instead of sugary cocktails (and still feel
                    social)
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-53">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-54" />
                  <span className="font-sans text-stone-600" data-id="element-55">
                    <strong className="font-700 text-forest" data-id="element-56">
                      The Morning-After Reset Protocol
                    </strong>{' '}
                    — wake up lighter even after a dinner out
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-57">
                <strong className="font-700 text-forest" data-id="element-58">
                  The result: You go to dinner. You enjoy it. You wake up
                  feeling lighter than before you went.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-59">
                <p className="font-serif text-forest italic text-lg" data-id="element-60">
                  "This guide alone is worth more than the entire investment
                  today."
                </p>
              </blockquote>
            </div>
          </div>
        </motion.article>

        {/* C. BONUS #2 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-12" data-id="element-61">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-62">
            <div className="order-1 md:order-1" data-id="element-63">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-64">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-65" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-66">
                  FREE BONUS #2 — Valued at $19
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-67">
                The 11 PM Emergency Snack List
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-68">
                "12 Cortisol-Lowering Snacks That Help You Sleep Deeper and Wake
                Up Lighter"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-69">
                <p data-id="element-70">It is 11 PM.</p>
                <p data-id="element-71">Your stomach is making that quiet, insistent sound.</p>
                <p data-id="element-72">
                  Every diet you have ever followed says:{' '}
                  <em data-id="element-73">drink water and go to bed.</em>
                </p>
                <p data-id="element-74">
                  But here is what that advice is actually doing to your body.
                </p>
                <p data-id="element-75">
                  When you go to bed with low blood sugar, your cortisol surges
                  to compensate. That surge is the exact reason you wake up at 3
                  AM with a racing mind and a tight midsection.
                </p>
                <p data-id="element-76">
                  <strong className="font-700 text-forest" data-id="element-77">
                    Going to bed hungry is not discipline. It is a cortisol
                    event.
                  </strong>
                </p>
                <p data-id="element-78">
                  <em data-id="element-79">The 11 PM Emergency Snack List</em> gives you{' '}
                  <strong className="font-700 text-forest" data-id="element-80">
                    12 specific snacks
                  </strong>{' '}
                  that do the opposite — they lower cortisol, stabilize blood
                  sugar, and trigger the deep, fat-burning REM sleep your body
                  has been missing.
                </p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-81">
                Inside this guide you will find:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-82">
                <li className="flex items-start gap-3" data-id="element-83">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-84" />
                  <span className="font-sans text-stone-600" data-id="element-85">
                    <strong className="font-700 text-forest" data-id="element-86">
                      The Safety Boat Snack
                    </strong>{' '}
                    — 1 ingredient, 60 seconds, signals your adrenal system to
                    stand down
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-87">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-88" />
                  <span className="font-sans text-stone-600" data-id="element-89">
                    <strong className="font-700 text-forest" data-id="element-90">
                      The Melatonin Precursor Combo
                    </strong>{' '}
                    — the exact nutrient pairing that puts your body into
                    fat-burning sleep mode
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-91">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-92" />
                  <span className="font-sans text-stone-600" data-id="element-93">
                    <strong className="font-700 text-forest" data-id="element-94">
                      The No-Go After-9 PM List
                    </strong>{' '}
                    — 3 foods that seem healthy but are secretly spiking your
                    cortisol all night
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-95">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-96" />
                  <span className="font-sans text-stone-600" data-id="element-97">
                    <strong className="font-700 text-forest" data-id="element-98">
                      The 4-Step Cortisol Shutdown Protocol
                    </strong>{' '}
                    — your new nightly closing ritual
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-99">
                <strong className="font-700 text-forest" data-id="element-100">
                  The result: You stop waking up at 3 AM. You stop feeling
                  guilty for being hungry. And you start waking up feeling
                  genuinely rested — and noticeably less puffy.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-101">
                <p className="font-serif text-forest italic text-lg" data-id="element-102">
                  "This is the guide I wish someone had given me 10 years ago."
                </p>
              </blockquote>
            </div>
            <div className="order-2 md:order-2" data-id="element-103">
              <img src="https://cdn.magicpatterns.com/uploads/nrkvf634uKhnnZZD2g4UAK/heylloyd-image-1773015622862_port_bono2.png" alt="The 11 PM Emergency Snack List" className="w-full rounded-2xl shadow-md" data-id="element-104" />
            </div>
          </div>
        </motion.article>

        {/* D. BONUS #3 */}
        <motion.article initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="bg-white rounded-2xl shadow-sm border border-sage/20 p-6 md:p-10 mb-16" data-id="element-105">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center" data-id="element-106">
            <div className="order-2 md:order-1" data-id="element-107">
              <img src="https://cdn.magicpatterns.com/uploads/mMtKQtynhoa89sdwDdop6t/heylloyd-image-1773015694376_port_bono_3.png" alt="The 7-Minute Metabolic Spark" className="w-full rounded-2xl shadow-md" data-id="element-108" />
            </div>
            <div className="order-1 md:order-2" data-id="element-109">
              <div className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-4 py-1.5 mb-6" data-id="element-110">
                <GiftIcon className="w-4 h-4 text-terracotta" aria-hidden="true" data-id="element-111" />
                <span className="font-sans text-xs font-700 tracking-widest uppercase" data-id="element-112">
                  FREE BONUS #3 — Valued at $29
                </span>
              </div>
              <h3 className="font-serif text-forest text-3xl font-700 mb-2" data-id="element-113">
                The 7-Minute Metabolic Spark
              </h3>
              <p className="font-sans text-stone-500 italic text-lg mb-6" data-id="element-114">
                "The Joint-Safe Morning Routine That Wakes Up Your Thyroid
                Without Spiking Your Stress Hormones"
              </p>

              <div className="font-sans text-stone-600 space-y-4 mb-6" data-id="element-115">
                <p data-id="element-116">
                  You already know that long cardio sessions are working against
                  you.
                </p>
                <p data-id="element-117">
                  <strong className="font-700 text-forest" data-id="element-118">
                    But here is the question nobody answers: if not that, then
                    what?
                  </strong>
                </p>
                <p data-id="element-119">The answer is not another gym membership.</p>
                <p data-id="element-120">
                  It is not a YouTube workout that has you jumping around at 6
                  AM.
                </p>
                <p data-id="element-121">It is 7 minutes. In your pajamas. Barefoot, if you want.</p>
                <p data-id="element-122">
                  <strong className="font-700 text-forest" data-id="element-123">
                    The 7-Minute Metabolic Spark
                  </strong>{' '}
                  is a cortisol-conscious movement protocol designed
                  specifically for women over 55 — to activate your largest
                  muscle groups, stimulate lymphatic drainage, and send a clear
                  signal to your thyroid: <em data-id="element-124">It is safe to burn now.</em>
                </p>
                <p data-id="element-125">No jumping. No equipment. No joint pain.</p>
              </div>

              <h4 className="font-sans font-700 text-forest text-lg mb-4" data-id="element-126">
                Inside this guide you will find:
              </h4>
              <ul className="space-y-3 mb-6" data-id="element-127">
                <li className="flex items-start gap-3" data-id="element-128">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-129" />
                  <span className="font-sans text-stone-600" data-id="element-130">
                    <strong className="font-700 text-forest" data-id="element-131">
                      The 4-Movement Release Circuit
                    </strong>{' '}
                    — 90 seconds each, zero impact, done in any room of your
                    house
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-132">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-133" />
                  <span className="font-sans text-stone-600" data-id="element-134">
                    <strong className="font-700 text-forest" data-id="element-135">
                      The 2 Thermal Window Timing Slots
                    </strong>{' '}
                    — the exact moments of day when these 7 minutes burn 3x more
                    effectively
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-136">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-137" />
                  <span className="font-sans text-stone-600" data-id="element-138">
                    <strong className="font-700 text-forest" data-id="element-139">
                      The Exhale Rhythm Rule
                    </strong>{' '}
                    — the one signal that tells you if you are helping or
                    hurting your cortisol levels
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-140">
                  <CheckIcon className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-141" />
                  <span className="font-sans text-stone-600" data-id="element-142">
                    <strong className="font-700 text-forest" data-id="element-143">
                      The Energy Check-In Guide
                    </strong>{' '}
                    — how to know, within 60 seconds of finishing, if your body
                    is responding
                  </span>
                </li>
              </ul>

              <p className="font-sans text-stone-600 mb-6" data-id="element-144">
                <strong className="font-700 text-forest" data-id="element-145">
                  The result: You move your body without punishing it. Your
                  midsection starts to feel less armored. And for the first time
                  in years, morning feels like a gift instead of a battle.
                </strong>
              </p>

              <blockquote className="bg-forest/5 border-l-4 border-forest p-4 rounded-r-lg" data-id="element-146">
                <p className="font-serif text-forest italic text-lg" data-id="element-147">
                  "Seven minutes. That is all it takes to change what your body
                  does for the next 17 hours."
                </p>
              </blockquote>
            </div>
          </div>
        </motion.article>

        {/* E. BONUS STACK SUMMARY TABLE */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-sage/20 overflow-hidden mb-16" data-id="element-148">
          <div className="bg-forest px-6 py-4 text-center" data-id="element-149">
            <h3 className="font-serif text-cream text-xl font-700" data-id="element-150">
              Bonus Summary
            </h3>
          </div>
          <div className="p-6" data-id="element-151">
            <div className="space-y-4" data-id="element-152">
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-153">
                <div className="flex items-center gap-3" data-id="element-154">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-155" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-156">
                    The Restaurant Survival Blueprint
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-157">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-158">
                    $27
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-159">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-160">
                <div className="flex items-center gap-3" data-id="element-161">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-162" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-163">
                    The 11 PM Emergency Snack List
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-164">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-165">
                    $19
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-166">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between border-b border-sage/20 pb-4" data-id="element-167">
                <div className="flex items-center gap-3" data-id="element-168">
                  <GiftIcon className="w-5 h-5 text-terracotta" aria-hidden="true" data-id="element-169" />
                  <span className="font-sans text-stone-700 font-700" data-id="element-170">
                    The 7-Minute Metabolic Spark
                  </span>
                </div>
                <div className="flex items-center gap-4 text-right" data-id="element-171">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-172">
                    $29
                  </span>
                  <span className="font-sans text-terracotta font-700" data-id="element-173">
                    FREE
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2" data-id="element-174">
                <span className="font-sans text-forest font-700 text-lg" data-id="element-175">
                  Total Bonus Value
                </span>
                <div className="flex items-center gap-4 text-right" data-id="element-176">
                  <span className="font-sans text-stone-400 line-through hidden sm:inline" data-id="element-177">
                    $75
                  </span>
                  <span className="font-sans text-terracotta font-700 text-xl" data-id="element-178">
                    FREE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* F. CLOSING TRANSITION COPY */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto font-sans text-stone-600 text-lg leading-relaxed space-y-4" data-id="element-179">
          <p data-id="element-180">So let us talk about what you are actually getting today.</p>
          <p data-id="element-181">
            You are getting <em data-id="element-182">The Cortisol Reset Kitchen</em> — the complete
            21-day protocol to reset your stress hormones, flatten your
            midsection, and finally feel at home in your body again.
          </p>
          <p data-id="element-183">
            <strong className="font-700 text-forest" data-id="element-184">
              You are getting $75 worth of companion guides
            </strong>{' '}
            that cover the three hardest real-life moments women over 55 face
            every single week.
          </p>
          <p data-id="element-185">
            And you are getting all of it for less than the cost of a single
            dinner out.
          </p>
        </motion.div>
      </div>
    </section>;
}